import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, FileUploaderDropContainer, FileUploaderItem, InlineNotification } from '@carbon/react';
import { TrashCan } from '@carbon/react/icons';
import { useUpload } from 'src/hooks/useUpload';
import { postMediaUploadUrl } from 'src/services/apis/services';
import { CreateUploadUrlDto } from 'src/services/apis/types';

const DEFAULT_ACCEPT = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];

/**
 * Must match `MAX_UPLOAD_BYTES` in apps/server/src/media/dto/createUploadUrl.dto.ts. The
 * presign already enforces this, but only after a round trip — checking here means an
 * oversize file never leaves the browser, and the user finds out immediately instead of
 * from a 400 that leaves the dropzone looking like nothing happened.
 */
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

interface FileListUploaderProps {
  value: string[];
  onChange: (keys: string[]) => void;
  label: string;
  accept?: string[];
}

/**
 * A list uploader for attachments: N files, no cropping, PDFs allowed alongside images.
 * Separate from `DNDUploader` on purpose — that one is single-value, always crops, and is
 * image-only, with ten call sites depending on that exact contract.
 *
 * Each file gets its own presign with no `thumbContentLength`: nothing here is cropped, so
 * there is no thumb source, and asking for one anyway would mint a signed PUT for an object
 * the browser never writes.
 */
const FileListUploader = React.memo(({ value, onChange, label, accept = DEFAULT_ACCEPT }: FileListUploaderProps) => {
  const { t } = useTranslation();
  const upload = useUpload();
  const [uploading, setUploading] = useState(false);
  const [oversizeMessage, setOversizeMessage] = useState<string | null>(null);

  const handleAddFiles = useCallback(
    async (_event: unknown, { addedFiles }: { addedFiles: (File & { invalidFileType?: boolean })[] }) => {
      const candidates = addedFiles.filter((file) => !file.invalidFileType);
      const files = candidates.filter((file) => file.size <= MAX_UPLOAD_BYTES);
      setOversizeMessage(files.length < candidates.length ? t('messages.fileIsTooBig', { size: '5MB' }) : null);
      if (files.length === 0) return;

      setUploading(true);
      try {
        const uploadedKeys: string[] = [];
        for (const file of files) {
          try {
            const { fileId, uploadUrl } = await postMediaUploadUrl({
              contentType: file.type as CreateUploadUrlDto['contentType'],
              contentLength: file.size,
            });
            await upload(uploadUrl, file);
            uploadedKeys.push(fileId);
          } catch {
            // A rejected presign or PUT must not add a key for an object that was never
            // written — a key with no object behind it renders as a permanently broken
            // attachment.
          }
        }
        if (uploadedKeys.length > 0) {
          onChange([...value, ...uploadedKeys]);
        }
      } finally {
        // Always runs, so a failure leaves the dropzone usable again instead of stuck
        // disabled — the defect CropperModal shipped with once, fixed the same way there.
        setUploading(false);
      }
    },
    [upload, value, onChange, t]
  );

  const handleRemove = useCallback(
    (index: number) => {
      onChange(value.filter((_key, position) => position !== index));
    },
    [value, onChange]
  );

  return (
    <div className="file-list-uploader">
      <p className="cds--label">{label}</p>
      {oversizeMessage && (
        <InlineNotification subtitle={oversizeMessage} onCloseButtonClick={() => setOversizeMessage(null)} />
      )}
      <FileUploaderDropContainer
        accept={accept}
        disabled={uploading}
        labelText={t('common.dndFilesHereOrClickToUpload')}
        multiple
        name=""
        onAddFiles={handleAddFiles}
      />
      <div className="file-list-uploader__items">
        {value.map((key, index) => (
          <div className="file-list-uploader__item" key={`${key}-${index}`}>
            <FileUploaderItem name={key} status="complete" size="md" />
            <Button
              hasIconOnly
              kind="ghost"
              size="sm"
              renderIcon={TrashCan}
              iconDescription={`${t('actions.deleteFile')} ${key}`}
              onClick={() => handleRemove(index)}
            />
          </div>
        ))}
      </div>
    </div>
  );
});

export default FileListUploader;
